export const EMPTY_TEXT = '未填写';

export const getLastValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[value.length - 1] : undefined;
  }
  return value;
};

export const formatDateValue = (value) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value.format === 'function') return value.format('YYYY-MM-DD');
  return undefined;
};

export const toMonthValue = (value) => {
  if (!value) return null;
  return String(value).slice(0, 7);
};

export const formatMonthRange = (from, to) => {
  const start = toMonthValue(from);
  const end = toMonthValue(to);
  if (!start) return '-';
  return `${start}~${end || '至今'}`;
};

export const getTreePath = (code, tree = [], currentPath = []) => {
  if (!code || !Array.isArray(tree)) return undefined;

  for (const node of tree) {
    const nextPath = [...currentPath, node.code];
    if (node.code === code) return nextPath;

    const childPath = getTreePath(code, node.children || [], nextPath);
    if (childPath) return childPath;
  }

  return undefined;
};

export const normalizeOption = (item) => ({
  ...item,
  label: item.label || item.name,
  value: item.value || item.code,
});

export const normalizeOptions = (values = []) => values.map(normalizeOption);

/**
 * 把 server 返回的职位树规范化为前端统一使用的形态:
 *   - 始终包含 `code` 字段 (字符串/数字均可), 即使 server 只返回 `value`/`id`
 *   - 始终包含 `name` 字段 (显示用)
 *   - 始终包含 `value` 字段 (向后兼容 Cascader)
 *
 * 这条函数是数据进入应用的第一道门 — 在这里把"server 字段命名不一致"消化掉,
 * 下游的所有消费者只需要查 `.code` / `.name`, 不必到处判空回退.
 *
 * 已知 server 实际字段名变种:
 *   - { name, code, children }      (标准)
 *   - { name, value, children }     (value 当 code 用, 类似你最初给我的 sample)
 *   - { name, id, children }        (少见, 防御性兜底)
 */
export const normalizeJobTree = (list = []) => list.map((item) => {
  // 关键修复: 兼容多种 server 字段名 — code/value/id 任一存在即视作 code
  const code = item.code ?? item.value ?? item.id ?? '';
  return {
    ...item,
    code,
    title: item.title || item.name,
    value: code,                       // Cascader/Modal 仍期望 value 在这里
    children: item.children ? normalizeJobTree(item.children) : [],
  };
});

export const normalizeEducationList = (educations = []) => educations.map((item) => ({
  ...item,
  ENRDATE: [toMonthValue(item.ENRFROM), toMonthValue(item.ENRTO)],
}));

export const normalizeOccupationList = (occupations = []) => occupations.map((item) => ({
  ...item,
  OCCDATE: [toMonthValue(item.OCCFROM), toMonthValue(item.OCCTO)],
}));

export const buildBaseInfoParams = (info, values, applicantId) => ({
  ...info,
  ...values,
  id: applicantId,
  BRTHDATE: formatDateValue(values.BRTHDATE),
  WKDATE: formatDateValue(values.WKDATE),
  REGDISTRICT: getLastValue(values.REGDISTRICT),
  RESDISTRICT: getLastValue(values.RESDISTRICT),
});

export const buildSimpleApplicantParams = (info, values, applicantId) => ({
  ...info,
  ...values,
  id: applicantId,
});

export const buildMonthRangeParams = (values, rangeField, fromField, toField) => ({
  [fromField]: values[rangeField] && values[rangeField][0] ? `${values[rangeField][0]}-01` : undefined,
  [toField]: values[rangeField] && values[rangeField][1] ? `${values[rangeField][1]}-01` : undefined,
  [rangeField]: undefined,
});

export const normalizeAttachmentIds = (attachments = []) => attachments
  .filter(Boolean)
  .map((item) => (typeof item === 'string' ? item : item.objId))
  .filter(Boolean);

export const buildFileList = (data, filePath) => {
  if (!data || !data.LOGO || data.LOGO.length === 0) return [];

  return [{
    uid: '-1',
    name: 'LOGO',
    status: 'done',
    url: `${filePath}applicant/${data.id}/M${data.LOGO[0]}.png`,
  }];
};
